import React from "react";
import { Popover, TextField, Button, Typography, Box, Radio, RadioGroup, FormControlLabel, Divider } from "@mui/material";
import Toggle from "../ui/toggle";
import { Search, ArrowDownAZ, ArrowUpAZ } from "lucide-react";
import { FilterParams } from "../transactions-table";

export type DescriptionFilterHandle = {
    applyNow: () => void;
    clear: () => void;
};

export interface DescriptionFilterProps {
    anchorEl?: HTMLElement | null;
    open?: boolean;
    onApply: (params: FilterParams | any) => React.SetStateAction<void>;
    onClose?: () => void;
    currentSort?: {
        sortBy?: string;
        sortDirection?: 'ASC' | 'DESC';
    };
    currentDescriptionKeyword?: string;
    // Lets other tables (e.g. invoices' ID column) reuse this same combined
    // sort+filter popover instead of duplicating it - defaults match the
    // original description-only behavior exactly.
    fieldLabel?: string;
    sortByValue?: string;
    placeholder?: string;
    // Renders the same sort/filter controls inline (no Popover/anchor) for
    // use inside a combined modal instead of an anchored popup.
    embedded?: boolean;
    // Hides this section's own Apply Now/Clear buttons - used when a
    // parent (the combined filter modal) drives Apply/Clear for every
    // section at once via the imperative handle below instead.
    hideActions?: boolean;
    // Optional extra toggle rendered below the filter field - lets one
    // specific caller (the Transactions page's "hide invoice-paid styling"
    // switch) add a control here without this shared, widely-reused
    // component knowing anything about what that toggle means.
    extraToggle?: { label: string; checked: boolean; onChange: (checked: boolean) => void };
}

const DescriptionFilter = React.forwardRef<DescriptionFilterHandle, DescriptionFilterProps>(({
    anchorEl,
    open,
    onApply,
    onClose,
    currentSort,
    currentDescriptionKeyword,
    fieldLabel = 'Description',
    sortByValue = 'description',
    placeholder = 'search description',
    embedded = false,
    hideActions = false,
    extraToggle,
}, ref) => {
    const [filterEnabled, setFilterEnabled] = React.useState<boolean>(!!currentDescriptionKeyword);
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc' | ''>(
        currentSort?.sortBy === sortByValue ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : ''
    );
    const [filterValue, setFilterValue] = React.useState<string>(currentDescriptionKeyword || '');

    // Update state when props change
    React.useEffect(() => {
        setFilterEnabled(!!currentDescriptionKeyword);
        setSortOrder(currentSort?.sortBy === sortByValue ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : '');
        setFilterValue(currentDescriptionKeyword || '');
    }, [currentDescriptionKeyword, currentSort, sortByValue]);

    const handleClear = () => {
        setFilterEnabled(false);
        setSortOrder('');
        setFilterValue('');
        const params: any = { descriptionKeyword: undefined };
        // Only signal a sort reset if this column currently owns the sort -
        // otherwise leave whatever sort is active on another column alone.
        if (currentSort?.sortBy === sortByValue) {
            params.sortBy = null;
        }
        onApply(params);
    }

    const handleApplyNow = () => {
        const params: any = {
            descriptionKeyword: filterEnabled ? filterValue : undefined,
        };
        if (sortOrder) {
            params.sortBy = sortByValue;
            params.sortDirection = sortOrder.toUpperCase() as 'ASC' | 'DESC';
        }
        onApply(params);
    };

    React.useImperativeHandle(ref, () => ({
        applyNow: handleApplyNow,
        clear: handleClear,
    }));

    const content = (
        <>
            {!embedded && <>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Sort & Filter</Typography>
                <Divider sx={{ my: 1 }}/>
            </>}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography sx={{ fontSize: 14 }}>Sort By {fieldLabel}</Typography>
            </Box>
            <RadioGroup value={sortOrder || ''} onChange={e => setSortOrder(e.target.value as 'asc' | 'desc')} sx={{ mb: 1, pl: 3 }}>
                <FormControlLabel
                    value="asc"
                    sx={{ height: 24 }}
                    control={<Radio size='small'/>}
                    label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowDownAZ size={14} />Sort A to Z</Typography>}
                />
                <FormControlLabel
                    value="desc"
                    sx={{ height: 24 }}
                    control={<Radio size='small'/>}
                    label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowUpAZ size={14} />Sort Z to A</Typography>}
                />
            </RadioGroup>
            <Divider sx={{ mb: 1 }}/>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Toggle checked={filterEnabled} onChange={setFilterEnabled} />
                <Typography sx={{ fontSize: 14 }}>Filter By {fieldLabel}</Typography>
            </Box>
            {filterEnabled && (
                <TextField
                    fullWidth
                    size="small"
                    placeholder={placeholder}
                    value={filterValue}
                    onChange={e => setFilterValue(e.target.value)}
                    onKeyPress={e => {
                        if (e.key === 'Enter') {
                            handleApplyNow();
                        }
                    }}
                    sx={{ mb: 1, mt: 0.5, borderRadius: 1 }}
                    InputProps={{
                        sx: { border: "none", boxShadow: "none", fontSize: 12 },
                        startAdornment: <Search size={16} color="var(--color-primary)" style={{ marginRight: 8 }} />,
                    }}
                />
            )}
            {extraToggle && (
                <>
                    <Divider sx={{ mb: 1 }}/>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Toggle checked={extraToggle.checked} onChange={extraToggle.onChange} />
                        <Typography sx={{ fontSize: 14 }}>{extraToggle.label}</Typography>
                    </Box>
                </>
            )}
            {!hideActions && (
                <Box display="flex" gap={1} mt={2}>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', whiteSpace: 'nowrap', '&:hover': { background: 'var(--color-primary)' } }}
                        onClick={handleApplyNow}
                    >
                        Apply Now
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', whiteSpace: 'nowrap', '&:hover': { background: 'var(--color-container-background)' } }}
                        onClick={handleClear}
                    >
                        Clear
                    </Button>
                </Box>
            )}
        </>
    );

    if (embedded) {
        return <Box>{content}</Box>;
    }

    return (
        <Popover
            open={!!open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{
                sx: {
                borderRadius: 2,
                boxShadow: 3,
                p: 2,
                minWidth: 300,
                },
            }}
        >
            {content}
        </Popover>
    );
});

export default DescriptionFilter;
